/**
 * @file router-settings-hub.tsx
 * @description Маршруты разделов «Настроек» кабинета магазина (phase19.31,
 * SC15; замечание владельца — «список слева, раздел справа»).
 *
 * Все разделы страницы «Настройки» собраны под одним каркасом
 * `SettingsHubLayout` (маршрут без своего адреса): с 1280px слева колонка
 * разделов, справа выбранный раздел (`Outlet`), ниже 1280 — список и раздел
 * отдельными экранами. Адреса разделов НЕ менялись (`users`,
 * `orders/pipelines/…`, `crm/templates` и т. д.) — прямые ссылки, закладки
 * и кнопка «назад» работают как раньше. Права на маршрутах — те же, что
 * были (каркас ничего не открывает сверх них).
 *
 * «Журнал уведомлений» (`settings/notifications`) — раздел зоны «Клиенты»,
 * не «Настроек»: его маршрут — в router.tsx, вне этого каркаса.
 */

import type { RouteObject } from "react-router";

import { RequirePermission } from "./components/guards/RequirePermission";
import { SettingsHubLayout } from "./layouts/SettingsHubLayout";
import { DocumentTemplatesPage } from "./pages/shop/crm/documents/DocumentTemplatesPage";
import { PipelineStagesPage } from "./pages/shop/crm/pipelines/PipelineStagesPage";
import { PipelinesPage } from "./pages/shop/crm/pipelines/PipelinesPage";
import { OrderPipelinesIndex } from "./pages/shop/orders/OrderPipelinesIndex";
import { OrderStagesPage } from "./pages/shop/orders/OrderStagesPage";
import { ReturnPipelinesIndex } from "./pages/shop/orders/ReturnPipelinesIndex";
import { ReturnStagesPage } from "./pages/shop/orders/ReturnStagesPage";
import { LegalDocumentEditPage } from "./pages/shop/settings/LegalDocumentEditPage";
import { LegalDocumentsListPage } from "./pages/shop/settings/LegalDocumentsListPage";
import { LegalProfilesPage } from "./pages/shop/settings/LegalProfilesPage";
import { NotificationTemplatesPage } from "./pages/shop/settings/NotificationTemplatesPage";
import { ShopDomainsSettingsPage } from "./pages/shop/settings/ShopDomainsSettingsPage";
import { ShopSettingsHubPage } from "./pages/shop/settings/ShopSettingsHubPage";
import { ShopSettingsPage } from "./pages/shop/ShopSettingsPage";
import { ShopUsersPage } from "./pages/shop/ShopUsersPage";

/** Каркас «Настроек» и его разделы — дочерний маршрут `ShopLayout`. */
export const settingsHubRoute: RouteObject = {
  element: <SettingsHubLayout />,
  children: [
    {
      path: "settings",
      children: [
        // Список разделов: ниже 1280 — отдельный экран; от 1280 каркас сразу
        // открывает первый доступный раздел.
        { index: true, element: <ShopSettingsHubPage /> },
        {
          element: <RequirePermission permissions={["SHOP_MANAGE_SETTINGS"]} />,
          children: [
            // «Магазин» (бывшие «Настройки магазина»).
            { path: "shop", element: <ShopSettingsPage /> },
            { path: "domains", element: <ShopDomainsSettingsPage /> },
            { path: "legal-profiles", element: <LegalProfilesPage /> },
            {
              path: "legal-documents",
              children: [
                { index: true, element: <LegalDocumentsListPage /> },
                { path: "new", element: <LegalDocumentEditPage /> },
                { path: ":id", element: <LegalDocumentEditPage /> },
              ],
            },
            { path: "notification-templates", element: <NotificationTemplatesPage /> },
          ],
        },
      ],
    },
    {
      path: "users",
      element: <RequirePermission permissions={["SHOP_MANAGE_USERS"]} />,
      children: [{ index: true, element: <ShopUsersPage /> }],
    },
    {
      // phase10.5 шаги 3 и 8 — стадии заказов и возвратов (права — как у «Заказов»).
      path: "orders",
      element: <RequirePermission permissions={["SHOP_VIEW_ORDERS"]} />,
      children: [
        // `pipelines` без id → перенаправление на стадии воронки по умолчанию.
        { path: "pipelines", element: <OrderPipelinesIndex /> },
        { path: "pipelines/:pipelineId/stages", element: <OrderStagesPage /> },
        { path: "returns/pipelines", element: <ReturnPipelinesIndex /> },
        { path: "returns/pipelines/:pipelineId/stages", element: <ReturnStagesPage /> },
      ],
    },
    {
      // Воронки сделок и шаблоны документов (права — как у CRM).
      path: "crm",
      element: <RequirePermission permissions={["SHOP_USE_CRM"]} />,
      children: [
        { path: "pipelines", element: <PipelinesPage /> },
        { path: "pipelines/:pipelineId", element: <PipelineStagesPage /> },
        {
          path: "templates",
          element: <RequirePermission permissions={["SHOP_MANAGE_CRM_CONFIG"]} />,
          children: [{ index: true, element: <DocumentTemplatesPage /> }],
        },
      ],
    },
  ],
};
